import React, { useState, useEffect } from 'react';
import { Play, X } from 'lucide-react';

interface WhiptVideoAdProps {
  onClose?: () => void;
  autoPlay?: boolean;
}

export const WhiptVideoAd: React.FC<WhiptVideoAdProps> = ({ 
  onClose, 
  autoPlay = true 
}) => {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [currentFrame, setCurrentFrame] = useState(0);

  // Animation frames (simulates video) - ACTUAL WHIPT Landing Page Gold/Black Style
  const frames = [
    {
      headline: "WHIPT",
      subtext: "Where'd I Put That!",
      gradient: "from-black via-gray-900 to-black",
      isIntro: true,
      goldText: true
    },
    {
      headline: "Scholarly Student Organizer",
      subtext: "Your intelligent life management system",
      gradient: "from-black via-amber-950 to-black",
      goldText: true
    },
    {
      headline: "Smart Organization",
      subtext: "Color-coded digital binder for all coursework",
      gradient: "from-black via-orange-950 to-black",
      goldText: true
    },
    {
      headline: "Real-Time Collaboration",
      subtext: "Video calls & instant messaging",
      gradient: "from-amber-950 via-orange-900 to-amber-950",
      goldText: true
    },
    {
      headline: "Document Hub",
      subtext: "Upload, organize, and export with ease",
      gradient: "from-orange-950 via-amber-900 to-orange-950",
      goldText: true
    }
  ];

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentFrame((prev) => (prev + 1) % frames.length);
    }, 3000); // Change frame every 3 seconds

    return () => clearInterval(interval);
  }, [isPlaying, frames.length]);

  const currentContent = frames[currentFrame];

  return (
    <div className="relative w-full bg-black rounded-lg overflow-hidden shadow-2xl">
      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-20 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
        >
          <X size={20} />
        </button>
      )}

      {/* Video-style ad container */}
      <div className="relative aspect-video">
        {/* Animated gradient background */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br ${currentContent.gradient} transition-all duration-1000`}
        >
          {/* Animated overlay pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
          </div>
        </div>

        {/* WHIPT Logo - REMOVED to prevent blocking content */}

        {/* Main content - animated - OPTIMIZED FOR iPhone 11 */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 sm:px-8 text-center">
          <h1 
            key={`headline-${currentFrame}`}
            className={`${
              currentContent.isIntro 
                ? 'text-5xl sm:text-7xl md:text-8xl font-black' 
                : 'text-3xl sm:text-4xl md:text-5xl font-bold'
            } bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400 bg-clip-text text-transparent drop-shadow-2xl mb-2 sm:mb-4 animate-fadeInUp leading-tight`}
          >
            {currentContent.headline}
          </h1>
          <p 
            key={`subtext-${currentFrame}`}
            className={`${
              currentContent.isIntro 
                ? 'text-lg sm:text-2xl md:text-3xl text-amber-200/70 font-semibold tracking-wider' 
                : 'text-base sm:text-xl md:text-2xl text-amber-200'
            } mb-4 sm:mb-8 animate-fadeInUp delay-200 px-2`}
          >
            {currentContent.subtext}
          </p>

          {/* Progress dots - Smaller on mobile */}
          <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-8">
            {frames.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                  idx === currentFrame 
                    ? 'w-6 sm:w-8 bg-white' 
                    : 'w-1.5 sm:w-2 bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Play/Pause overlay (if not auto-playing) */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <button
              onClick={() => setIsPlaying(true)}
              className="bg-white/90 hover:bg-white p-6 rounded-full shadow-2xl transform hover:scale-110 transition-all"
            >
              <Play size={40} className="text-gray-900 ml-1" />
            </button>
          </div>
        )}

        {/* Subtle animated particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white/30 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${3 + i}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom branding bar - Gold/Black Theme - Optimized for mobile */}
      <div className="bg-gradient-to-r from-black via-amber-950 to-black px-4 py-2 sm:px-6 sm:py-3 flex items-center justify-between border-t border-amber-900/30">
        <p className="text-amber-200 text-xs sm:text-sm font-semibold">
          Trusted by 10,000+ students
        </p>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-amber-400 rounded-full animate-pulse"></div>
          <span className="text-amber-200 text-xs sm:text-sm font-bold">Live Now</span>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          50% {
            opacity: 0.6;
          }
          100% {
            transform: translateY(-100vh) translateX(50px);
            opacity: 0;
          }
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .animate-float {
          animation: float linear infinite;
        }

        .delay-200 {
          animation-delay: 0.2s;
        }

        .delay-400 {
          animation-delay: 0.4s;
        }
      `}</style>
    </div>
  );
};

export default WhiptVideoAd;
